import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { format, parseISO, isAfter, isBefore, startOfDay } from "date-fns";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Plus, Truck, Package, Search, Filter,
  ArrowUpDown, ArrowUp, ArrowDown, ArrowUpRight, ArrowDownLeft
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_LIST = ["Planned", "Ready to Unload", "On Hold", "Loading", "Shipped"];
const statusCfg = {
  Planned:          { bg: "bg-sky-50 text-sky-700 border-sky-200", dot: "bg-sky-500" },
  "Ready to Unload":{ bg: "bg-teal-50 text-teal-700 border-teal-200", dot: "bg-teal-500" },
  "On Hold":        { bg: "bg-red-50 text-red-700 border-red-200", dot: "bg-red-500" },
  Loading:          { bg: "bg-amber-50 text-amber-700 border-amber-200", dot: "bg-amber-500" },
  Shipped:          { bg: "bg-emerald-50 text-emerald-700 border-emerald-200", dot: "bg-emerald-500" },
};

const StatusBadge = ({ status }) => {
  const c = statusCfg[status] || statusCfg.Planned;
  return <span data-testid={`status-badge-${status?.toLowerCase().replace(/ /g,"-")}`} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold border whitespace-nowrap shrink-0 ${c.bg}`}><span className={`w-1.5 h-1.5 rounded-full ${c.dot}`} />{status}</span>;
};

export default function InternalTransfersPage() {
  const navigate = useNavigate();
  const [loads, setLoads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refData, setRefData] = useState(null);

  // Filters
  const [search, setSearch] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [fStatus, setFStatus] = useState([]);
  const [fVehicle, setFVehicle] = useState("");
  const [fCarrier, setFCarrier] = useState("");
  const [fDateFrom, setFDateFrom] = useState("");
  const [fDateTo, setFDateTo] = useState("");
  const [fShipTo, setFShipTo] = useState("");

  // Sort
  const [sortCol, setSortCol] = useState("date");
  const [sortDir, setSortDir] = useState("desc");

  // Modal
  const [modal, setModal] = useState(null);

  const fetchLoads = useCallback(async () => {
    try { const r = await axios.get(`${API}/internal-transfers`); setLoads(r.data); } catch (e) { console.error(e); } finally { setLoading(false); }
  }, []);
  const fetchRef = useCallback(async () => {
    try { const r = await axios.get(`${API}/internal-transfers/reference-data`); setRefData(r.data); } catch {}
  }, []);

  useEffect(() => { fetchLoads(); fetchRef(); }, [fetchLoads, fetchRef]);

  // Filter + Sort
  const activeFilterCount = fStatus.length + (fVehicle ? 1 : 0) + (fCarrier ? 1 : 0) + (fDateFrom ? 1 : 0) + (fDateTo ? 1 : 0) + (fShipTo ? 1 : 0);
  const clearFilters = () => { setFStatus([]); setFVehicle(""); setFCarrier(""); setFDateFrom(""); setFDateTo(""); setFShipTo(""); };

  const filtered = useMemo(() => {
    return loads.filter(l => {
      if (search) { const q = search.toLowerCase(); if (!l.description?.toLowerCase().includes(q) && !l.vehicle_id?.toLowerCase().includes(q) && !l.carrier?.toLowerCase().includes(q)) return false; }
      if (fStatus.length > 0 && !fStatus.includes(l.status)) return false;
      if (fVehicle && l.vehicle_id !== fVehicle) return false;
      if (fCarrier && l.carrier !== fCarrier) return false;
      if (fShipTo && !l.ship_to?.includes(fShipTo)) return false;
      if (fDateFrom) { try { if (isBefore(parseISO(l.date), startOfDay(parseISO(fDateFrom)))) return false; } catch {} }
      if (fDateTo) { try { if (isAfter(parseISO(l.date), startOfDay(parseISO(fDateTo)))) return false; } catch {} }
      return true;
    }).sort((a, b) => {
      let va = a[sortCol] ?? "", vb = b[sortCol] ?? "";
      if (sortCol === "pallet_count") { va = a.pallet_count || 0; vb = b.pallet_count || 0; }
      if (typeof va === "number") return sortDir === "asc" ? va - vb : vb - va;
      return sortDir === "asc" ? String(va).localeCompare(String(vb)) : String(vb).localeCompare(String(va));
    });
  }, [loads, search, fStatus, fVehicle, fCarrier, fShipTo, fDateFrom, fDateTo, sortCol, sortDir]);

  const toggleSort = (col) => { if (sortCol === col) setSortDir(d => d === "asc" ? "desc" : "asc"); else { setSortCol(col); setSortDir("desc"); } };

  const SortHeader = ({ col, label, className = "" }) => (
    <th className={`px-3 py-2 font-medium cursor-pointer select-none hover:bg-zinc-100 transition-colors ${className}`} onClick={() => toggleSort(col)} data-testid={`sort-${col}`}>
      <div className="flex items-center gap-1">
        {label}
        {sortCol === col ? (sortDir === "asc" ? <ArrowUp className="w-3 h-3 text-zinc-900" /> : <ArrowDown className="w-3 h-3 text-zinc-900" />) : <ArrowUpDown className="w-3 h-3 text-zinc-300" />}
      </div>
    </th>
  );

  const fmtDate = (d) => { if (!d) return "—"; try { return format(parseISO(d), "MMM d, yyyy"); } catch { return d; } };

  return (
    <div className="flex flex-col h-[calc(100vh-60px)] overflow-hidden" data-testid="internal-transfers-page">
      {/* HEADER BAR */}
      <div className="px-4 py-2.5 border-b border-zinc-200 bg-white shrink-0">
        <div className="flex items-center justify-between mb-2">
          <h1 className="text-base font-bold text-zinc-900">Internal Transfers</h1>
          <Button size="sm" onClick={() => setModal("create")} data-testid="new-truck-load-btn" className="h-8 gap-1 text-xs"><Plus className="w-3.5 h-3.5" /> New Truck Load</Button>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-400" />
            <Input placeholder="Search description, vehicle, carrier..." className="h-8 pl-8 text-xs" value={search} onChange={e => setSearch(e.target.value)} data-testid="transfer-search-input" />
          </div>
          <Button size="sm" variant={showFilters ? "default" : "outline"} onClick={() => setShowFilters(!showFilters)} className="h-8 px-2.5 text-xs gap-1 relative" data-testid="toggle-filters-btn">
            <Filter className="w-3.5 h-3.5" /> Filters
            {activeFilterCount > 0 && <span className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-red-500 text-white text-[10px] flex items-center justify-center">{activeFilterCount}</span>}
          </Button>
          <div className="text-[11px] text-zinc-400 ml-2">{filtered.length} of {loads.length} loads</div>
        </div>
        {/* Filter panel */}
        {showFilters && (
          <div className="mt-2 bg-zinc-50 border border-zinc-200 rounded-lg p-3 space-y-2.5" data-testid="filter-panel">
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-semibold text-zinc-500 uppercase">Filters</span>
              {activeFilterCount > 0 && <button onClick={clearFilters} className="text-[11px] text-red-500 hover:underline" data-testid="clear-filters-btn">Clear all</button>}
            </div>
            <div>
              <Label className="text-[10px] text-zinc-500">Status</Label>
              <div className="flex flex-wrap gap-1 mt-0.5">
                {STATUS_LIST.map(s => (
                  <button key={s} onClick={() => setFStatus(p => p.includes(s) ? p.filter(x => x !== s) : [...p, s])} data-testid={`filter-status-${s.toLowerCase().replace(/ /g,"-")}`}
                    className={`px-2 py-0.5 rounded-full text-[10px] font-medium border transition-colors ${fStatus.includes(s) ? (statusCfg[s]?.bg || "") : "bg-white text-zinc-500 border-zinc-200"}`}>{s}</button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <div><Label className="text-[10px] text-zinc-500">Date From</Label><Input type="date" value={fDateFrom} onChange={e => setFDateFrom(e.target.value)} className="h-7 text-xs" data-testid="filter-date-from" /></div>
              <div><Label className="text-[10px] text-zinc-500">Date To</Label><Input type="date" value={fDateTo} onChange={e => setFDateTo(e.target.value)} className="h-7 text-xs" data-testid="filter-date-to" /></div>
              <div><Label className="text-[10px] text-zinc-500">Vehicle</Label>
                <Select value={fVehicle} onValueChange={v => setFVehicle(v === "all" ? "" : v)}><SelectTrigger className="h-7 text-xs" data-testid="filter-vehicle"><SelectValue placeholder="All" /></SelectTrigger>
                  <SelectContent><SelectItem value="all">All</SelectItem>{(refData?.vehicles || []).filter(v => v != null && v !== "").map(v => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent></Select>
              </div>
              <div><Label className="text-[10px] text-zinc-500">Carrier</Label>
                <Select value={fCarrier} onValueChange={v => setFCarrier(v === "all" ? "" : v)}><SelectTrigger className="h-7 text-xs" data-testid="filter-carrier"><SelectValue placeholder="All" /></SelectTrigger>
                  <SelectContent><SelectItem value="all">All</SelectItem>{(refData?.carriers || []).filter(c => c != null && c !== "").map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select>
              </div>
            </div>
            <div><Label className="text-[10px] text-zinc-500">Ship To</Label>
              <Select value={fShipTo} onValueChange={v => setFShipTo(v === "all" ? "" : v)}><SelectTrigger className="h-7 text-xs" data-testid="filter-shipto"><SelectValue placeholder="All destinations" /></SelectTrigger>
                <SelectContent><SelectItem value="all">All</SelectItem>{(refData?.ship_to_options || []).filter(s => s != null && s !== "").map(s => <SelectItem key={s} value={s}>{s.split(" - ")[0]}</SelectItem>)}</SelectContent></Select>
            </div>
          </div>
        )}
      </div>

      {/* TABLE — full width, row click navigates to detail page */}
      <div className="flex-1 overflow-auto bg-white">
        <table className="w-full text-xs" data-testid="transfers-table">
          <thead className="bg-zinc-50 text-zinc-600 sticky top-0 z-10">
            <tr>
              <SortHeader col="to_number" label="TO #" className="text-left" />
              <SortHeader col="date" label="Date" className="text-left" />
              <SortHeader col="description" label="Description" className="text-left" />
              <SortHeader col="status" label="Status" className="text-left" />
              <th className="px-3 py-2 text-left font-medium">Direction</th>
              <SortHeader col="vehicle_id" label="Vehicle" className="text-left" />
              <SortHeader col="carrier" label="Carrier" className="text-left hidden lg:table-cell" />
              <SortHeader col="pallet_count" label="Pallets" className="text-right" />
              <th className="px-3 py-2 text-left font-medium hidden xl:table-cell">Ship To</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={9} className="text-center py-8 text-zinc-400">Loading...</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={9} className="text-center py-8 text-zinc-400">No transfers found</td></tr>
            ) : filtered.map(l => (
              <tr key={l.truck_load_id} onClick={() => navigate(`/internal-transfers/${l.truck_load_id}`)} data-testid={`transfer-row-${l.truck_load_id}`}
                className="border-b border-zinc-100 cursor-pointer hover:bg-zinc-50 transition-colors">
                <td className="px-3 py-2.5 font-mono text-[11px] font-semibold text-blue-700">{l.to_number || "—"}</td>
                <td className="px-3 py-2.5 whitespace-nowrap">{fmtDate(l.date)}</td>
                <td className="px-3 py-2.5 max-w-[220px] truncate font-medium text-zinc-900">{l.description}</td>
                <td className="px-3 py-2.5"><StatusBadge status={l.status} /></td>
                <td className="px-3 py-2.5"><DirectionBadge direction={l.direction} /></td>
                <td className="px-3 py-2.5 font-mono text-[11px]">{l.vehicle_id}</td>
                <td className="px-3 py-2.5 hidden lg:table-cell text-zinc-500 truncate max-w-[120px]">{l.carrier || "—"}</td>
                <td className="px-3 py-2.5 text-right font-semibold">{l.pallet_count || 0}</td>
                <td className="px-3 py-2.5 hidden xl:table-cell text-zinc-500 truncate max-w-[160px]">{l.ship_to?.split(" - ")[0] || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* CREATE MODAL */}
      {modal === "create" && <CreateModal open onClose={() => setModal(null)} refData={refData} onSaved={d => { navigate(`/internal-transfers/${d.truck_load_id}`); }} />}
    </div>
  );
}

function DirectionBadge({ direction }) {
  const isOut = direction !== "inbound";
  return <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border ${isOut ? "bg-blue-50 text-blue-700 border-blue-200" : "bg-purple-50 text-purple-700 border-purple-200"}`}>{isOut ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownLeft className="w-3 h-3" />}{isOut ? "Out" : "In"}</span>;
}

function CreateModal({ open, onClose, refData, onSaved }) {
  const [form, setForm] = useState({ date: format(new Date(), "yyyy-MM-dd"), description: "", vehicle_id: "", truck_load_type: "Outbound Transfer", direction: "outbound", carrier: "", ship_to: "" });
  const [saving, setSaving] = useState(false);
  const vehicles = refData?.vehicles || []; const carriers = refData?.carriers || []; const shipTos = refData?.ship_to_options || [];
  const handleSave = async () => {
    if (!form.description) { toast.error("Description is required"); return; }
    setSaving(true);
    try { const r = await axios.post(`${API}/internal-transfers`, form); toast.success("Created"); onSaved(r.data); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onClose}><DialogContent className="max-w-md" data-testid="create-truck-load-modal"><DialogHeader><DialogTitle>New Truck Load</DialogTitle></DialogHeader>
      <div className="space-y-3 mt-1">
        <div><Label className="text-xs">Direction *</Label>
          <div className="flex gap-2 mt-1">{["outbound","inbound"].map(d => <button key={d} onClick={() => setForm({...form, direction: d})} data-testid={`direction-${d}`}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-lg border text-xs font-medium transition-colors ${form.direction === d ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-600 border-zinc-300 hover:bg-zinc-50"}`}>
            {d === "outbound" ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownLeft className="w-4 h-4" />}{d.charAt(0).toUpperCase() + d.slice(1)}
          </button>)}</div>
        </div>
        <div><Label className="text-xs">Date *</Label><Input type="date" value={form.date} onChange={e => setForm({...form, date: e.target.value})} className="h-9" data-testid="truck-load-date-input" /></div>
        <div><Label className="text-xs">Description *</Label><Input value={form.description} onChange={e => setForm({...form, description: e.target.value})} className="h-9" placeholder="e.g., Circle K Burritos to Southwest" data-testid="truck-load-description-input" /></div>
        <div><Label className="text-xs">Vehicle ID <span className="text-zinc-400">(optional)</span></Label><div className="flex flex-wrap gap-1.5 mt-1"><button onClick={() => setForm({...form, vehicle_id: ""})} className={`px-2.5 py-1 rounded text-xs border ${!form.vehicle_id ? "bg-zinc-200 text-zinc-700 border-zinc-300" : "bg-white text-zinc-600 border-zinc-300"}`}>None</button>{vehicles.map(v => <button key={v} onClick={() => setForm({...form, vehicle_id: v})} data-testid={`vehicle-btn-${v}`}
          className={`px-2.5 py-1 rounded text-xs font-mono border ${form.vehicle_id === v ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-600 border-zinc-300"}`}>{v}</button>)}</div></div>
        <div className="grid grid-cols-2 gap-2">
          <div><Label className="text-xs">Carrier</Label><Select value={form.carrier} onValueChange={v => setForm({...form, carrier: v})}><SelectTrigger className="h-9 text-xs" data-testid="truck-load-carrier-select"><SelectValue placeholder="Select" /></SelectTrigger><SelectContent>{carriers.filter(c => c != null && c !== "").map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select></div>
          <div><Label className="text-xs">Ship To</Label><Select value={form.ship_to} onValueChange={v => setForm({...form, ship_to: v})}><SelectTrigger className="h-9 text-xs" data-testid="truck-load-shipto-select"><SelectValue placeholder="Select" /></SelectTrigger><SelectContent>{shipTos.filter(s => s != null && s !== "").map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select></div>
        </div>
      </div>
      <div className="flex justify-end gap-2 mt-3"><Button variant="outline" size="sm" onClick={onClose}>Cancel</Button><Button size="sm" onClick={handleSave} disabled={saving} data-testid="save-truck-load-btn">{saving ? "..." : "Create"}</Button></div>
    </DialogContent></Dialog>
  );
}

